<template>
  <div class="form-step">


    <!-- Awards Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Awards received since EDCTP Fellowship</h5>
      <button type="button" class="btn btn-outline-primary" @click="addAward">
        <i class="bi bi-plus me-2"></i>Add Award
      </button>
    </div>

    <div v-if="awardDraft" class="award-item mb-4 p-3 border rounded bg-light-subtle">
      <div class="row my-3">
        <div class="col-md-6">
          <label class="form-label">Year</label>
          <input ref="newAwardYearInput" type="date" class="form-control" :class="{ 'field-invalid': isAwardFieldInvalid(awardDraft, 'year', null, true) }" v-model="awardDraft.year" @input="handleAwardDraftInput">
        </div>
        <div class="col-md-6">
          <label class="form-label">Awarding body</label>
          <input type="text" class="form-control" :class="{ 'field-invalid': isAwardFieldInvalid(awardDraft, 'university', null, true) }" v-model="awardDraft.university" @input="handleAwardDraftInput">
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="cancelNewAward">Cancel</button>
          <button type="button" class="btn btn-outline-primary" @click="saveNewAward">Save</button>
        </div>
      </div>
    </div>

    <div v-for="(award, index) in awards" :key="`award-${awardKey(award, index)}`" class="award-item mb-4 p-3 border rounded">
      <div
        class="award-header d-flex justify-content-between align-items-center"
        role="button"
        tabindex="0"
        @click="toggleAward(awardKey(award, index))"
        @keydown.enter.prevent="toggleAward(awardKey(award, index))"
        @keydown.space.prevent="toggleAward(awardKey(award, index))"
      >
        <h6 class="mb-0 border-0 pb-0">{{ awardHeaderTitles[awardKey(award, index)] }}</h6>
        <i class="bi" :class="isAwardExpanded(awardKey(award, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>
      <div v-if="isAwardExpanded(awardKey(award, index))" class="row my-3">
        <div class="col-md-6">
          <label class="form-label">Year</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isAwardFieldInvalid(award, 'year', index) }"
            v-model="award.year"
            @input="markAwardDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">Awarding body</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isAwardFieldInvalid(award, 'university', index) }"
            v-model="award.university"
            @input="markAwardDirty(index)"
          >
        </div>
      </div>
      <div v-if="isAwardExpanded(awardKey(award, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!awardDirty[index]" @click="saveAward(index)">
            <i class="bi bi-check me-1"></i>Save
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeAward(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Other Experiences Section -->
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Professional Activities</h5>
      <button type="button" class="btn btn-outline-primary" @click="addOtherExperience">
        <i class="bi bi-plus me-2"></i>Add Activity
      </button>
    </div>

      <div v-if="otherExperienceDraft" class="experience-item mb-4 p-3 border rounded bg-light-subtle">
        <div class="row my-3">
          <div class="col-md-12">
            <label class="form-label">Role</label>
            <input ref="newOtherExperiencePositionInput" type="text" class="form-control" :class="{ 'field-invalid': isOtherExperienceFieldInvalid(otherExperienceDraft, 'position', null, true) }" v-model="otherExperienceDraft.position" @input="handleOtherExperienceDraftInput" placeholder="Fieldwork, projects, technical work, committee work etc.">
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Start date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isOtherExperienceFieldInvalid(otherExperienceDraft, 'startDate', null, true) }" v-model="otherExperienceDraft.startDate" @input="handleOtherExperienceDraftInput">
          </div>
          <div class="col-md-6">
            <label class="form-label">End date</label>
            <input type="date" class="form-control" :class="{ 'field-invalid': isOtherExperienceFieldInvalid(otherExperienceDraft, 'endDate', null, true) }" v-model="otherExperienceDraft.endDate" @input="handleOtherExperienceDraftInput">
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="cancelNewOtherExperience">Cancel</button>
            <button type="button" class="btn btn-outline-primary" @click="saveNewOtherExperience">Save</button>
          </div>
        </div>
      </div>

      <div v-for="(experience, index) in otherExperiences" :key="`experience-${otherExperienceKey(experience, index)}`" class="experience-item mb-4 p-3 border rounded">
        <div
          class="experience-header d-flex justify-content-between align-items-center"
          role="button"
          tabindex="0"
          @click="toggleOtherExperience(otherExperienceKey(experience, index))"
          @keydown.enter.prevent="toggleOtherExperience(otherExperienceKey(experience, index))"
          @keydown.space.prevent="toggleOtherExperience(otherExperienceKey(experience, index))"
        >
          <h6 class="mb-0 border-0 pb-0">{{ otherExperienceHeaderTitles[otherExperienceKey(experience, index)] }}</h6>
          <i class="bi" :class="isOtherExperienceExpanded(otherExperienceKey(experience, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-if="isOtherExperienceExpanded(otherExperienceKey(experience, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Position</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'field-invalid': isOtherExperienceFieldInvalid(experience, 'position', index) }"
            v-model="experience.position"
            @input="markOtherExperienceDirty(index)"
          >
        </div>
      </div>
        <div v-if="isOtherExperienceExpanded(otherExperienceKey(experience, index))" class="row mb-3">
        <div class="col-md-6">
          <label class="form-label">Start date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isOtherExperienceFieldInvalid(experience, 'startDate', index) }"
            v-model="experience.startDate"
            @input="markOtherExperienceDirty(index)"
          >
        </div>
        <div class="col-md-6">
          <label class="form-label">End date</label>
          <input
            type="date"
            class="form-control"
            :class="{ 'field-invalid': isOtherExperienceFieldInvalid(experience, 'endDate', index) }"
            v-model="experience.endDate"
            @input="markOtherExperienceDirty(index)"
          >
        </div>
      </div>
        <div v-if="isOtherExperienceExpanded(otherExperienceKey(experience, index))" class="row">
          <div class="col-md-12 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!otherExperienceDirty[index]" @click="saveOtherExperience(index)">
              <i class="bi bi-check me-1"></i>Save
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger" @click="removeOtherExperience(index)">
              <i class="bi bi-x me-1"></i>Remove
            </button>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { parseDateFields } from '@/utils/dateUtils'

export default {
  name: 'ExperiencesForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update', 'pending-state-change'],
  setup(props, { emit }) {
    // Reactive refs for each section
    const awards = ref([])
    const expandedAwardIds = ref([])
    const awardDraft = ref(null)
    const awardDraftAttempted = ref(false)
    const awardDirty = ref({})
    const awardAttempted = ref({})
    const newAwardYearInput = ref(null)
    const otherExperiences = ref([])
    const expandedOtherExperienceIds = ref([])
    const otherExperienceDraft = ref(null)
    const otherExperienceDraftAttempted = ref(false)
    const otherExperienceDirty = ref({})
    const otherExperienceAttempted = ref({})
    const newOtherExperiencePositionInput = ref(null)

    const normalizeHeaderText = (value) => {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const getYear = (value) => {
      const dateValue = typeof value === 'string' ? value.trim() : ''
      const match = dateValue.match(/^(\d{4})/)
      return match ? match[1] : ''
    }

    const hasInvalidDateRange = (startValue, endValue) => {
      if (!startValue || !endValue) return false
      return new Date(startValue) > new Date(endValue)
    }

    const awardKey = (award, index) => {
      if (!award) return `new-${index}`
      return award.id || award._localKey || `new-${index}`
    }

    const awardHeaderTitles = computed(() => {
      return awards.value.reduce((titles, award, index) => {
        const key = awardKey(award, index)
        const university = normalizeHeaderText(award?.university)
        const year = getYear(award?.year)

        if (!university && !year) {
          titles[key] = `New Award ${index + 1}`
          return titles
        }

        titles[key] = [university, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    })

    const otherExperienceKey = (experience, index) => {
      if (!experience) return `new-${index}`
      return experience.id || experience._localKey || `new-${index}`
    }

    const otherExperienceHeaderTitles = computed(() => {
      return otherExperiences.value.reduce((titles, experience, index) => {
        const key = otherExperienceKey(experience, index)
        const position = normalizeHeaderText(experience?.position)
        const year = getYear(experience?.startDate)

        if (!position && !year) {
          titles[key] = `New Other Experience ${index + 1}`
          return titles
        }

        titles[key] = [position, year].filter(Boolean).join(' | ')
        return titles
      }, {})
    })

    // Initialize data from props
    const initializeData = () => {
      // Initialize from formData first
      if (props.formData.awards?.length > 0) {
        awards.value = props.formData.awards.map(award => ({
          id: award.id || null,
          _localKey: award.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          year: award.year || '',
          university: award.university || ''
        }))
      }

      if (props.formData.otherExperiences?.length > 0) {
        const experiences = props.formData.otherExperiences.map(experience => ({
          id: experience.id || null,
          _localKey: experience.id ? null : `${Date.now()}-${Math.random()}`,
          _isNew: false,
          position: experience.position || '',
          startDate: experience.start_date || experience.startDate || '',
          endDate: experience.end_date || experience.endDate || ''
        }))
        otherExperiences.value = parseDateFields(experiences)
      }

      // Override with profile data if available
      if (props.profileData?.data?.personal) {
        const fellowData = props.profileData.data.personal

        // Parse awards if it's a string
        if (fellowData.awards) {
          try {
            const parsedAwards = typeof fellowData.awards === 'string'
              ? JSON.parse(fellowData.awards)
              : fellowData.awards

            if (Array.isArray(parsedAwards) && parsedAwards.length > 0) {
              awards.value = parsedAwards.map(award => ({
                id: award.id || null,
                _localKey: award.id ? null : `${Date.now()}-${Math.random()}`,
                _isNew: false,
                year: award.year || '',
                university: award.university || ''
              }))
            }
          } catch (e) {
            console.warn('Error parsing awards:', e)
          }
        }

        if (fellowData.otherExperiences?.length > 0) {
          const experiences = fellowData.otherExperiences.map(experience => ({
            id: experience.id || null,
            _localKey: experience.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            position: experience.position || '',
            startDate: experience.start_date || experience.startDate || '',
            endDate: experience.end_date || experience.endDate || ''
          }))
          otherExperiences.value = parseDateFields(experiences)
        }
      }

      expandedAwardIds.value = awards.value
        .map((award, index) => (award._isNew ? awardKey(award, index) : null))
        .filter(Boolean)
      expandedOtherExperienceIds.value = otherExperiences.value
        .map((experience, index) => (experience._isNew ? otherExperienceKey(experience, index) : null))
        .filter(Boolean)
    }

    // Emit current data to parent
    const emitUpdate = () => {
      // Ensure numeric/date fields are properly formatted
      const sanitizedAwards = awards.value.map(award => ({
        ...award,
        // Convert empty year strings to null for proper date validation
        year: award.year && String(award.year).trim() ? award.year : null
      }))

      const data = {
        awards: sanitizedAwards,
        otherExperiences: otherExperiences.value
      }

      emit('update', data)
    }

    // Add methods
    const addAward = () => {
      if (awardDraft.value) {
        nextTick(() => {
          if (newAwardYearInput.value) {
            newAwardYearInput.value.focus()
          }
        })
        return
      }

      awardDraft.value = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        year: '',
        university: ''
      }

      nextTick(() => {
        if (newAwardYearInput.value) {
          newAwardYearInput.value.focus()
        }
      })
    }

    const cancelNewAward = () => {
      awardDraft.value = null
    }

    const isAwardValid = (award) => {
      if (!award) return false
      return Boolean(String(award.year || '').trim() && String(award.university || '').trim())
    }

    const saveNewAward = () => {
      if (!isAwardValid(awardDraft.value)) {
        awardDraftAttempted.value = true
        return
      }

      awards.value.unshift({ ...awardDraft.value })
      const key = awardKey(awards.value[0], 0)
      if (key && !expandedAwardIds.value.includes(key)) {
        expandedAwardIds.value.push(key)
      }
      awardDraft.value = null
      awardDraftAttempted.value = false
      emitUpdate()
    }

    const isAwardFieldInvalid = (award, field, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? awardDraftAttempted.value : Boolean(awardAttempted.value[index])
      if (!shouldValidate) return false

      if (field === 'year') return !String(award?.year || '').trim()
      if (field === 'university') return !String(award?.university || '').trim()
      return false
    }

    const handleAwardDraftInput = () => {
      if (awardDraftAttempted.value && isAwardValid(awardDraft.value)) {
        awardDraftAttempted.value = false
      }
    }

    const markAwardDirty = (index) => {
      awardDirty.value = {
        ...awardDirty.value,
        [index]: true
      }

      if (awardAttempted.value[index] && isAwardValid(awards.value[index])) {
        awardAttempted.value = {
          ...awardAttempted.value,
          [index]: false
        }
      }
    }

    const saveAward = (index) => {
      if (!isAwardValid(awards.value[index])) {
        awardAttempted.value = {
          ...awardAttempted.value,
          [index]: true
        }
        return
      }

      awardDirty.value = {
        ...awardDirty.value,
        [index]: false
      }
      awardAttempted.value = {
        ...awardAttempted.value,
        [index]: false
      }
      emitUpdate()
    }

    const removeAward = (index) => {
      const removed = awards.value[index]
      awards.value.splice(index, 1)
      const removedKey = awardKey(removed, index)
      expandedAwardIds.value = expandedAwardIds.value.filter((id) => id !== removedKey)
      const nextDirty = {}
      Object.keys(awardDirty.value).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = awardDirty.value[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = awardDirty.value[key]
        }
      })
      awardDirty.value = nextDirty
      const nextAttempted = {}
      Object.keys(awardAttempted.value).forEach((key) => {
        const attemptedIndex = Number(key)
        if (attemptedIndex < index) {
          nextAttempted[attemptedIndex] = awardAttempted.value[key]
        }
        if (attemptedIndex > index) {
          nextAttempted[attemptedIndex - 1] = awardAttempted.value[key]
        }
      })
      awardAttempted.value = nextAttempted
      emitUpdate()
    }

    const toggleAward = (key) => {
      if (!key) return

      if (expandedAwardIds.value.includes(key)) {
        expandedAwardIds.value = expandedAwardIds.value.filter((id) => id !== key)
      } else {
        expandedAwardIds.value.push(key)
      }
    }

    const isAwardExpanded = (key) => expandedAwardIds.value.includes(key)

    const addOtherExperience = () => {
      if (otherExperienceDraft.value) {
        nextTick(() => {
          if (newOtherExperiencePositionInput.value) {
            newOtherExperiencePositionInput.value.focus()
          }
        })
        return
      }

      otherExperienceDraft.value = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        position: '',
        startDate: '',
        endDate: ''
      }

      nextTick(() => {
        if (newOtherExperiencePositionInput.value) {
          newOtherExperiencePositionInput.value.focus()
        }
      })
    }

    const cancelNewOtherExperience = () => {
      otherExperienceDraft.value = null
    }

    const isOtherExperienceValid = (experience) => {
      if (!experience) return false

      return Boolean(
        String(experience.position || '').trim() &&
        String(experience.startDate || '').trim() &&
        String(experience.endDate || '').trim() &&
        !hasInvalidDateRange(experience.startDate, experience.endDate)
      )
    }

    const saveNewOtherExperience = () => {
      if (!isOtherExperienceValid(otherExperienceDraft.value)) {
        otherExperienceDraftAttempted.value = true
        return
      }

      otherExperiences.value.unshift({ ...otherExperienceDraft.value })
      const key = otherExperienceKey(otherExperiences.value[0], 0)
      if (key && !expandedOtherExperienceIds.value.includes(key)) {
        expandedOtherExperienceIds.value.push(key)
      }
      otherExperienceDraft.value = null
      otherExperienceDraftAttempted.value = false
      emitUpdate()
    }

    const isOtherExperienceFieldInvalid = (experience, field, index = null, isDraft = false) => {
      const shouldValidate = isDraft ? otherExperienceDraftAttempted.value : Boolean(otherExperienceAttempted.value[index])
      if (!shouldValidate) return false

      if (field === 'position') return !String(experience?.position || '').trim()
      if (field === 'startDate') return !String(experience?.startDate || '').trim()
      if (field === 'endDate') return !String(experience?.endDate || '').trim() || hasInvalidDateRange(experience?.startDate, experience?.endDate)
      return false
    }

    const handleOtherExperienceDraftInput = () => {
      if (otherExperienceDraftAttempted.value && isOtherExperienceValid(otherExperienceDraft.value)) {
        otherExperienceDraftAttempted.value = false
      }
    }

    const markOtherExperienceDirty = (index) => {
      otherExperienceDirty.value = {
        ...otherExperienceDirty.value,
        [index]: true
      }

      if (otherExperienceAttempted.value[index] && isOtherExperienceValid(otherExperiences.value[index])) {
        otherExperienceAttempted.value = {
          ...otherExperienceAttempted.value,
          [index]: false
        }
      }
    }

    const saveOtherExperience = (index) => {
      if (!isOtherExperienceValid(otherExperiences.value[index])) {
        otherExperienceAttempted.value = {
          ...otherExperienceAttempted.value,
          [index]: true
        }
        return
      }

      otherExperienceDirty.value = {
        ...otherExperienceDirty.value,
        [index]: false
      }
      otherExperienceAttempted.value = {
        ...otherExperienceAttempted.value,
        [index]: false
      }
      emitUpdate()
    }

    const removeOtherExperience = (index) => {
      const removed = otherExperiences.value[index]
      otherExperiences.value.splice(index, 1)
      const removedKey = otherExperienceKey(removed, index)
      expandedOtherExperienceIds.value = expandedOtherExperienceIds.value.filter((id) => id !== removedKey)
      const nextDirty = {}
      Object.keys(otherExperienceDirty.value).forEach((key) => {
        const dirtyIndex = Number(key)
        if (dirtyIndex < index) {
          nextDirty[dirtyIndex] = otherExperienceDirty.value[key]
        }
        if (dirtyIndex > index) {
          nextDirty[dirtyIndex - 1] = otherExperienceDirty.value[key]
        }
      })
      otherExperienceDirty.value = nextDirty
      const nextAttempted = {}
      Object.keys(otherExperienceAttempted.value).forEach((key) => {
        const attemptedIndex = Number(key)
        if (attemptedIndex < index) {
          nextAttempted[attemptedIndex] = otherExperienceAttempted.value[key]
        }
        if (attemptedIndex > index) {
          nextAttempted[attemptedIndex - 1] = otherExperienceAttempted.value[key]
        }
      })
      otherExperienceAttempted.value = nextAttempted
      emitUpdate()
    }

    const toggleOtherExperience = (key) => {
      if (!key) return

      if (expandedOtherExperienceIds.value.includes(key)) {
        expandedOtherExperienceIds.value = expandedOtherExperienceIds.value.filter((id) => id !== key)
      } else {
        expandedOtherExperienceIds.value.push(key)
      }
    }

    const isOtherExperienceExpanded = (key) => expandedOtherExperienceIds.value.includes(key)

    const hasPendingChanges = computed(() => {
      return Boolean(awardDraft.value) ||
        Boolean(otherExperienceDraft.value) ||
        Object.values(awardDirty.value).some(Boolean) ||
        Object.values(otherExperienceDirty.value).some(Boolean)
    })

    watch(hasPendingChanges, (isPending) => {
      emit('pending-state-change', isPending)
    }, { immediate: true })

    // Initialize on mount
    onMounted(() => {
      initializeData()
    })

    return {
      awards,
      awardHeaderTitles,
      awardKey,
      toggleAward,
      isAwardExpanded,
      otherExperiences,
      otherExperienceHeaderTitles,
      otherExperienceKey,
      toggleOtherExperience,
      isOtherExperienceExpanded,
      addAward,
      cancelNewAward,
      saveNewAward,
      handleAwardDraftInput,
      markAwardDirty,
      saveAward,
      removeAward,
      isAwardFieldInvalid,
      addOtherExperience,
      cancelNewOtherExperience,
      saveNewOtherExperience,
      handleOtherExperienceDraftInput,
      markOtherExperienceDirty,
      saveOtherExperience,
      removeOtherExperience,
      isOtherExperienceFieldInvalid,
      emitUpdate,
      awardDraft,
      awardDirty,
      newAwardYearInput,
      isAwardValid,
      otherExperienceDraft,
      otherExperienceDirty,
      newOtherExperiencePositionInput,
      isOtherExperienceValid,
      hasPendingChanges
    }
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.award-item,
.experience-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.award-header {
  cursor: pointer;
  user-select: none;
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
